'use client';

import React from 'react';
import { Apple, Play } from 'lucide-react';
import { brand } from '@/config/brand';
import { useTranslations } from '@/lib/i18n-client';
import { isPlaceholderValue } from '@/lib/contact';

interface AppStoreWidgetProps {
  className?: string;
  theme?: 'light' | 'dark' | 'auto';
  layout?: 'vertical' | 'horizontal';
}

export default function AppStoreWidget({ 
  className = '', 
  theme = 'auto',
  layout = 'horizontal' 
}: AppStoreWidgetProps) {
  const { t } = useTranslations();
  
  // Replace these with actual brand config if available in the future
  const appStoreUrl = ''; // brand.social.appStore
  const playStoreUrl = ''; // brand.social.playStore

  const hasAppStore = !isPlaceholderValue(appStoreUrl) && appStoreUrl !== '';
  const hasPlayStore = !isPlaceholderValue(playStoreUrl) && playStoreUrl !== '';

  const themeClasses = theme === 'dark' 
    ? 'bg-black text-white hover:bg-zinc-900 border-zinc-800' 
    : theme === 'light'
      ? 'bg-white text-black hover:bg-zinc-50 border-zinc-200 shadow-sm'
      : 'bg-black text-white dark:bg-white dark:text-black hover:opacity-90 border-transparent';

  const layoutClasses = layout === 'vertical' ? 'flex-col' : 'flex-row flex-wrap';

  // Fallback UI when apps aren't launched yet
  if (!hasAppStore && !hasPlayStore) {
    return (
      <div className={`p-4 rounded-xl border border-dashed border-border bg-muted/30 text-center ${className}`}>
        <div className="flex justify-center gap-2 text-muted-foreground mb-2">
          <Apple className="w-5 h-5" />
          <Play className="w-5 h-5" />
        </div>
        <h3 className="text-sm font-semibold text-foreground mb-1">
          {t('appWidget.comingSoon.title', 'Mobile Apps Coming Soon')}
        </h3>
        <p className="text-xs text-muted-foreground max-w-[200px] mx-auto">
          {t('appWidget.comingSoon.desc', 'We are working hard on our iOS and Android apps. Stay tuned!')}
        </p>
      </div>
    );
  }

  return (
    <div className={`flex gap-3 ${layoutClasses} ${className}`}>
      {hasAppStore && (
        <a 
          href={appStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-3 px-4 py-2 rounded-xl border transition-all duration-200 ${themeClasses}`}
        >
          <Apple className="w-6 h-6" />
          <div className="flex flex-col items-start leading-none">
            <span className="text-[10px] uppercase font-medium opacity-80 mb-0.5">
              {t('appWidget.downloadOn', 'Download on the')}
            </span>
            <span className="text-sm font-semibold tracking-tight">
              App Store
            </span>
          </div>
        </a>
      )}

      {hasPlayStore && (
        <a 
          href={playStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-3 px-4 py-2 rounded-xl border transition-all duration-200 ${themeClasses}`}
        >
          <Play className="w-6 h-6" />
          <div className="flex flex-col items-start leading-none">
            <span className="text-[10px] uppercase font-medium opacity-80 mb-0.5">
              {t('appWidget.getItOn', 'GET IT ON')}
            </span>
            <span className="text-sm font-semibold tracking-tight">
              Google Play
            </span>
          </div>
        </a>
      )}
    </div>
  );
}
